import React, { useState } from 'react';
import { Search, Loader, FilterX, List, Table, Plus, Pencil, Save, X, Trash2 } from 'lucide-react';
import ActionMenu from './ActionMenu';
import DownloadModal from './DownloadModal';
import ColumnSettingsModal from './ColumnSettingsModal';

const SearchIcon = ({ searchTerm, onClear }) => {
  const [hovered, setHovered] = useState(false);
  const showX = hovered && !!searchTerm;
  return (
    <span
      className={`search-icon${searchTerm ? ' search-icon-clearable' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={showX ? onClear : undefined}
    >
      {showX ? <X size={16} /> : <Search size={16} />}
    </span>
  );
};

const ModuleHeader = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  searchPlaceholder,
  filters,
  clearAllFilters,
  filteredCount,
  totalCount,
  columns,
  filteredData,
  filename,
  // CRUD props for list view
  onAdd,
  onDelete,
  onEdit,
  onSave,
  onCancel,
  canEdit = true,
  canSave = false,
  canCancel = false,
  isEditing = false,
  isNewRecord = false,
  // Datasheet props
  editModeEnabled = false,
  onEditModeToggle,
  isDirty = false,
  onDatasheetSave,
  onDatasheetCancel,
  datasheetSaving = false,
  // Column settings props
  columnOrder,
  hiddenColumns,
  onColumnSettingsChange,
  // Permission
  userCanEdit = true,
  canDownload = true,
  // Delete mode props
  deleteModeEnabled = false,
  selectedDeleteCount = 0,
  onEnterDeleteMode,
  onCancelDeleteMode,
  onConfirmDelete,
  onSearchSubmit,
  searchLoading = false,
  forceViewType = null,
  headerTabs = null
}) => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const hasActiveFilters = Object.values(filters).some(f => f?.length > 0);

  return (
    <div className="module-header">
      <div className="module-header-left">
        {headerTabs ? (
          <div className="module-header-tabs">{headerTabs}</div>
        ) : (
          <div className="module-header-tabs">
            {!forceViewType && <button
              onClick={() => setViewMode('list')}
              className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              <List size={18} />
            </button>}
            {!forceViewType && <button
              onClick={() => setViewMode('datasheet')}
              className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`}
              title="Datasheet View"
            >
              <Table size={18} />
            </button>}
          </div>
        )}
      </div>

      <div className="module-header-actions">
        {/* CRUD Buttons - List View Only, hidden for Guest users */}
        {viewMode === 'list' && userCanEdit && (
          <div className="module-header-crud">
            {isEditing ? (
              onDelete && (
                <button
                  onClick={onDelete}
                  className={`crud-btn delete-btn ${isNewRecord ? 'disabled' : ''}`}
                  title="Delete Record"
                  disabled={isNewRecord}
                >
                  <Trash2 size={18} />
                </button>
              )
            ) : (
              onAdd && (
                <button
                  onClick={onAdd}
                  className="crud-btn"
                  title="Add New Record"
                >
                  <Plus size={18} />
                </button>
              )
            )}
            {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
            <button
              onClick={isEditing ? onCancel : onEdit}
              className={`crud-btn ${!canEdit && !isEditing ? 'disabled' : ''}`}
              title={isEditing ? 'Cancel' : 'Edit Record'}
              disabled={!canEdit && !isEditing}
            >
              {isEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
            <button
              onClick={onSave}
              className={`crud-btn ${!canSave ? 'disabled' : ''}`}
              title="Save Changes"
              disabled={!canSave}
            >
              <Save size={18} />
            </button>
          </div>
        )}

        {/* Datasheet Controls - Datasheet View Only */}
        {viewMode === 'datasheet' && (
          <div className="module-header-datasheet-controls">
            {/* Delete Mode Controls - replaces Edit/Save when in delete mode */}
            {deleteModeEnabled && userCanEdit && (
              <div className="module-header-crud">
                <button
                  onClick={onCancelDeleteMode}
                  className="crud-btn"
                  title="Cancel Delete Mode"
                >
                  <X size={18} />
                </button>
                <button
                  onClick={onConfirmDelete}
                  className={`crud-btn delete-btn ${selectedDeleteCount === 0 ? 'disabled' : ''}`}
                  title={selectedDeleteCount > 0 ? `Delete ${selectedDeleteCount} selected` : 'Select rows to delete'}
                  disabled={selectedDeleteCount === 0}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            )}
            {/* Edit/Save - hidden for Guest users, hidden in delete mode */}
            {!deleteModeEnabled && userCanEdit && onEditModeToggle && (
              <div className="module-header-crud">
                {/* Edit/Cancel toggle - Pencil when not editing, X when editing */}
                <button
                  onClick={onEditModeToggle}
                  className="crud-btn"
                  title={editModeEnabled ? 'Cancel' : 'Enable Editing'}
                  disabled={datasheetSaving}
                >
                  {editModeEnabled ? <X size={18} /> : <Pencil size={18} />}
                </button>
                <button
                  onClick={onDatasheetSave}
                  className={`crud-btn ${!isDirty || datasheetSaving ? 'disabled' : ''}`}
                  title="Save Changes"
                  disabled={!isDirty || datasheetSaving}
                >
                  {datasheetSaving ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Clear filter — immediately left of search, no reserved space */}
        {hasActiveFilters && (
          <button onClick={clearAllFilters} title="Clear all filters" className="clear-filter-btn">
            <FilterX size={16} />
            <span>{filteredCount}/{totalCount}</span>
          </button>
        )}

        <div className="search-wrapper">
          {searchLoading
            ? <Loader size={16} className="search-icon spinning" />
            : <SearchIcon
                searchTerm={searchTerm}
                onClear={() => { setSearchTerm(''); onSearchSubmit?.(''); }}
              />
          }
          <input
            type="text"
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              if (!e.target.value) onSearchSubmit?.('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchSubmit?.(e.target.value);
              }
            }}
            placeholder={searchPlaceholder}
            className="search-input"
          />
        </div>
        <ActionMenu
          disabled={viewMode === 'list'}
          onDownload={canDownload ? () => setShowDownloadModal(true) : undefined}
          onModifyView={() => setShowColumnSettings(true)}
          showModifyView={viewMode === 'datasheet'}
          onBulkDelete={onEnterDeleteMode}
          showBulkDelete={viewMode === 'datasheet' && userCanEdit && !deleteModeEnabled && !editModeEnabled && !!onEnterDeleteMode}
        />
      </div>
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        data={filteredData}
        columns={columns}
        tableName={filename}
      />
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        onApply={onColumnSettingsChange}
      />
    </div>
  );
};

export default ModuleHeader;
